/**
 * Phone-number helpers — keep `tel:` / `sms:` link generation in one
 * place so the display number from siteConfig.supportPhone is the only
 * source of truth. Hardcoded numbers anywhere else are a bug — admin
 * updates supportPhone in /admin/settings and the change should
 * propagate everywhere on next render.
 */

/**
 * Strip a display phone like "(347) 452-3279" or "347-452-3279" down
 * to digits-only. Always returns a US-prefixed tel: href:
 *   "(347) 452-3279" → "tel:+13474523279"
 *   "+1 (347) 452-3279" → "tel:+13474523279"
 *   "3474523279" → "tel:+13474523279"
 *
 * Pass an empty string to get an inert `tel:` (avoids breaking the
 * markup if siteConfig hasn't loaded yet).
 */
export function phoneToTelHref(displayPhone: string | undefined | null): string {
  if (!displayPhone) return "tel:";
  const digits = displayPhone.replace(/\D/g, "");
  if (digits.length === 0) return "tel:";
  // Prepend a US country code if the caller didn't supply one. Falls
  // through cleanly for 11-digit numbers that already start with 1.
  const withCountry = digits.startsWith("1") ? digits : `1${digits}`;
  return `tel:+${withCountry}`;
}

/**
 * SMS variant — same digits, optional pre-filled body. Encodes the
 * body so spaces, ampersands, and Hebrew/Arabic glyphs all survive.
 */
export function phoneToSmsHref(
  displayPhone: string | undefined | null,
  body?: string
): string {
  if (!displayPhone) return "sms:";
  const digits = displayPhone.replace(/\D/g, "");
  if (digits.length === 0) return "sms:";
  const withCountry = digits.startsWith("1") ? digits : `1${digits}`;
  const base = `sms:+${withCountry}`;
  if (!body) return base;
  return `${base}?&body=${encodeURIComponent(body)}`;
}

/**
 * Normalize an admin- or customer-entered phone to E.164 (e.g. "+13474567890")
 * for Twilio. Returns null when the input can't be parsed as a real
 * phone number. Use this at every server-side intake point — DO NOT
 * accept raw `phone` strings into the DB without running them through
 * here, or you'll end up with 9-digit ghosts like APT-096's
 * `347831221` that Twilio rejects with error 21211 hours later.
 *
 * Accepts:
 *   "(347) 456-7890"     → "+13474567890"
 *   "347-456-7890"       → "+13474567890"
 *   "3474567890"         → "+13474567890"
 *   "13474567890"        → "+13474567890"
 *   "+13474567890"       → "+13474567890"
 *   "+44 20 7946 0958"   → "+442079460958"  (any 8-15 digit intl)
 *
 * Rejects (returns null):
 *   "347831221"          (9 digits — short by 1, dropped a digit)
 *   "555-5555"           (7 digits — way short)
 *   "abc"                (no digits)
 *   ""                   (empty / whitespace)
 *   "0"                  (zero digits after trim)
 */
export function normalizePhoneE164(
  input: string | null | undefined
): string | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;
  if (/^\+\d{8,15}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 0) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  return null;
}

/**
 * True iff the input parses as a valid phone. Convenience wrapper —
 * use this in route validators when you don't need the normalized
 * form yet, e.g. as a Zod refinement.
 */
export function isValidPhone(input: string | null | undefined): boolean {
  return normalizePhoneE164(input) !== null;
}
